import { useMemo } from "react";
import { Pressable } from "react-native";
import { useUnistyles } from "react-native-unistyles";
import { View, Text } from "@src/components";
import type { ConfigScenario } from "../utils/config-scenarios";
import { ScenarioStatusPill, ScenarioStalePill, ScenarioExcludedPill } from "./ScenarioStatusPill";

interface ConfigScenarioListProps {
  scenarios: ConfigScenario[];
  selectedScenarioUuid: string | null;
  onSelect: (scenarioUuid: string) => void;
}

/**
 * Section 2 of the config page: every scenario in the selected site, with the
 * missing ones surfaced first (design doc §4b — "missing ones visually
 * emphasised"). This is the operator's between-scenario movement mechanism:
 * clicking a row selects it (the parent turns that into a URL change), which
 * re-seeds the map/form and enables the data section.
 *
 * Sort is missing-before-configured, preserving each group's original
 * (backend) order within, so the operator's outstanding work floats to the top
 * without shuffling unpredictably as they configure scenarios.
 */
export function ConfigScenarioList({ scenarios, selectedScenarioUuid, onSelect }: ConfigScenarioListProps) {
  const { theme } = useUnistyles();
  const isDark = theme.currentTheme === "dark";

  // Selection treatment: a brand-blue left accent bar plus a light-blue tint.
  // The old `surfaceCardInner` grey was invisible because it matches the page
  // background; blue clearly reads as "this is the one you're editing" and is
  // distinct from the grey header row. Tint is theme-aware so it stays legible
  // in both modes.
  const selectedBg = isDark ? theme.colors.blue[800] : theme.colors.blue[50];
  const selectedAccent = isDark ? theme.colors.blue[400] : theme.colors.blue[500];

  const ordered = useMemo(() => {
    const missing = scenarios.filter((s) => s.status === "missing");
    const configured = scenarios.filter((s) => s.status === "configured");
    return [...missing, ...configured];
  }, [scenarios]);

  return (
    <View
      style={{
        borderRadius: theme.spacing.md,
        borderWidth: 1,
        borderColor: theme.colors.borderCard,
        overflow: "hidden",
      }}
    >
      <View
        style={{
          paddingHorizontal: theme.spacing.xl,
          paddingVertical: theme.spacing.md,
          backgroundColor: theme.colors.surfaceCard,
          borderBottomWidth: 1,
          borderBottomColor: theme.colors.borderCard,
        }}
      >
        <Text textStyle="xs/semibold" color="textMuted">
          Scenarios ({scenarios.length})
        </Text>
      </View>
      {ordered.map((scenario) => {
        const isSelected = scenario.scenarioUuid === selectedScenarioUuid;
        return (
          <Pressable
            key={scenario.key}
            onPress={() => onSelect(scenario.scenarioUuid)}
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: theme.spacing.md,
              paddingHorizontal: theme.spacing.xl,
              paddingVertical: theme.spacing.lg,
              borderBottomWidth: 1,
              borderBottomColor: theme.colors.borderCard,
              // Left accent bar marks the selected row; width is always reserved
              // (transparent when unselected) so text doesn't shift on selection.
              borderLeftWidth: theme.spacing.xs,
              borderLeftColor: isSelected ? selectedAccent : "transparent",
              backgroundColor: isSelected ? selectedBg : "transparent",
            }}
          >
            <View flex={1} style={{ gap: theme.spacing.xxs }}>
              <Text textStyle={isSelected ? "sm/semibold" : "sm/regular"} color="text" numberOfLines={1}>
                {scenario.scenarioName}
              </Text>
              <Text textStyle="xs/regular" color="textMuted" numberOfLines={1}>
                {scenario.assetCameraLabel}
              </Text>
            </View>
            {/* Primary status pill plus independent stale/excluded signals; the
                group wraps so the extra pills reflow beneath on a narrow panel. */}
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                flexShrink: 0,
                flexWrap: "wrap",
                justifyContent: "flex-end",
                gap: theme.spacing.xs,
              }}
            >
              <ScenarioStatusPill status={scenario.status} />
              {scenario.stale ?
                <ScenarioStalePill />
              : null}
              {scenario.excluded ?
                <ScenarioExcludedPill />
              : null}
            </View>
          </Pressable>
        );
      })}
    </View>
  );
}
